import type {
  FilterValueKindValue,
  ConditionSourceValue,
  FilterOperatorValue,
  ComputedFieldValue,
  OrgConfigFeatureTypeValue,
  BetweenValueType,
} from "@whatslly/types/admin-tools/OrgExplorer";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";

// Centralized computed field definitions with metadata
// This is the single source of truth for computed field names and their value types
export const COMPUTED_FIELD_CONFIGS = {
  total_users: { valueType: "number" as const },
  active_users: { valueType: "number" as const },
  sidecar_users: { valueType: "number" as const },
  desktop_users: { valueType: "number" as const },
  last_activity: { valueType: "date" as const },
} satisfies Record<string, { valueType: FilterValueKindValue }>;

// Array of all computed field values for iteration
export const ComputedFieldValues = Object.keys(COMPUTED_FIELD_CONFIGS) as ComputedFieldValue[];

// Helper to get value type for a computed field
export const getComputedFieldValueType = (field: ComputedFieldValue): FilterValueKindValue => COMPUTED_FIELD_CONFIGS[field].valueType;

// Helper to convert org config feature type to filter value kind
// Centralized here to avoid duplication between frontend and backend
export const getValueKindFromOrgConfigType = (configType: OrgConfigFeatureTypeValue | undefined): FilterValueKindValue => {
  if (!configType) return "unknown";
  switch (configType) {
    case "boolean":
      return "boolean";
    case "numeric":
      return "number";
    case "select":
      return "select";
    case "multiSelect":
    case "stringArray":
      return "multi";
    case "date":
      return "date";
    case "string":
    case "longText":
    case "encryptedString":
      return "string";
    case "legacyObject":
    case "perUserObject":
    case "other":
    default:
      return "unknown";
  }
};

// Centralized filter operator definitions with metadata
export const FILTER_OPERATOR_CONFIGS = {
  eq: { translationKey: "operator_eq" },
  ne: { translationKey: "operator_ne" },
  gt: { translationKey: "operator_gt" },
  gte: { translationKey: "operator_gte" },
  lt: { translationKey: "operator_lt" },
  lte: { translationKey: "operator_lte" },
  contains: { translationKey: "operator_contains" },
  starts_with: { translationKey: "operator_starts_with" },
  not_contains: { translationKey: "operator_not_contains" },
  between: { translationKey: "operator_between" },
  includes_all: { translationKey: "operator_includes_all" },
  includes_any: { translationKey: "operator_includes_any" },
  excludes_all: { translationKey: "operator_excludes_all" },
} as const satisfies Record<FilterOperatorValue, { translationKey: string }>;

// Array of all filter operator values for iteration
export const FilterOperatorValues = Object.keys(FILTER_OPERATOR_CONFIGS) as FilterOperatorValue[];

// Human-friendly operators by use case (maps to FilterOperatorValue)
export const NUMBER_OPERATORS = ["gt", "lt", "between", "eq"] as const;
export const DATE_OPERATORS = ["lt", "gt", "between", "eq"] as const; // lt = before, gt = after
export const BOOLEAN_OPERATORS = ["eq"] as const; // "is"
export const INTEGRATION_OPERATORS = ["includes_all", "includes_any", "excludes_all"] as const;

// Helper to get appropriate operators for a given value type and condition source
export const getOperatorsForValueType = (valueType: FilterValueKindValue, source: ConditionSourceValue): readonly FilterOperatorValue[] => {
  if (source === "integration") return INTEGRATION_OPERATORS;
  if (valueType === "number") return NUMBER_OPERATORS;
  if (valueType === "date") return DATE_OPERATORS;
  if (valueType === "boolean") return BOOLEAN_OPERATORS;
  // Default for string/select/multi/unknown
  return BOOLEAN_OPERATORS;
};

export const NON_DESTINATION_INTEGRATION_CONFIGS = {
  "platforms:slack": { name: "Slack", integrationType: DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS },
  "platforms:teams": { name: "Teams App", integrationType: DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS },
  "channels:waba": { name: "WhatsApp Business API (phones assigned)", integrationType: DESTINATION_INTEGRATION_TYPES.CHANNELS },
  "channels:twilio": { name: "Tuvis Call Recording (phones assigned)", integrationType: DESTINATION_INTEGRATION_TYPES.CHANNELS },
  "archive:teams": { name: "Teams Archive", integrationType: DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS },
  "archive:discord": { name: "Discord Archive", integrationType: DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS },
} as const;

export type NonDestinationIntegrationType = keyof typeof NON_DESTINATION_INTEGRATION_CONFIGS;

// Helper to check if value is a BetweenValue
export const isBetweenValue = (value: unknown): value is BetweenValueType => {
  return typeof value === "object" && value !== null && "min" in value && "max" in value;
};

// Value normalization utilities (shared between frontend and backend)
export const normalizeBooleanValue = (value: unknown): boolean | null => {
  if (value === true || value === "true" || value === 1) return true;
  if (value === false || value === "false" || value === 0) return false;
  return null;
};

export const normalizeNumberValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// Returns timestamp in milliseconds or null
export const normalizeDateValue = (value: unknown): number | null => {
  if (typeof value === "string" && value.trim() !== "") {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? null : timestamp;
  }
  return null;
};

// Validate between value (both min and max must be valid for the given type)
export const isValidBetweenValue = (value: unknown, valueType: "number" | "date"): boolean => {
  if (!isBetweenValue(value)) return false;
  if (valueType === "number") {
    return normalizeNumberValue(value.min) !== null && normalizeNumberValue(value.max) !== null;
  }
  return normalizeDateValue(value.min) !== null && normalizeDateValue(value.max) !== null;
};
